import JSBridge, { type JSBridgeProtocol } from '@realsee/jsbridge-x'
import { BrowserRTC, BrowserRTC4Chime, RTCProtocol, VRWebViewRTC } from '../rtc'
import {
  BuiltInRTCType,
  Command,
  EventType,
  GetVoiceSign,
  KeyframeEventType,
  LiveOptions,
  RoomInfo,
  RTCType,
  UserInfo,
  ReqLiveMsg,
  BuiltinEventType,
  BuiltinActionType,
  MicroStatus,
  LiveMsg,
  BuiltinMsg,
  LiveConnectOptions,
} from '../typings'
import { LiveState } from '../typings/live/LiveState'
import { appendUri } from '../utils/appendUri'
import { isRealseeVRWebview, isAndroid, isIOS, isWxMiniProgram } from '../utils/clientDetector'
import { equal } from '../utils/equal'
import logger from '../utils/logger'
import { Subscribe } from '../utils/subscribe'
import { uuid } from '../utils/uuid'
import { LiveController } from './controller'
import * as Y from 'yjs'

const unimplementedGetVoiceSign: GetVoiceSign = (params) =>
  Promise.reject(new Error('unimplemented method GetVoiceSign'))

export type LiveBuiltInData<KeyframesSnapshot> = {
  selfInfo?: UserInfo
  userList?: UserInfo[]
  roomInfo?: RoomInfo
  rtcInfo?: { voiceId: string; type: RTCType }
  keyframes: Y.Map<KeyframesSnapshot>
}

const createLiveMsg = (
  command: Command,
  data: Record<string, any> = {},
  toUserIds: string | string[] = [],
): ReqLiveMsg => ({
  command,
  front_request_id: uuid(),
  data: {
    ...data,
    to_user_ids: Array.isArray(toUserIds) ? toUserIds : [toUserIds],
  },
})

export const _clearLiveInstanceIfNeed = () => {
  if ((window as any)?.__Realsee_Live__) {
    ;(window as any)?.__Realsee_Live__?.dispose?.()
    ;(window as any).__Realsee_Live__ = null
  }
}

/**
 * Live 带看实例类
 * 
 * 这是带看功能的核心类，负责管理 WebSocket 连接、帧数据同步、RTC 语音通信等功能。
 * 通过 Yjs 实现帧数据的协同编辑和同步。
 * 
 * @template KeyframesSnapshot 帧数据快照类型，必须是一个对象类型
 * @example
 * ```typescript
 * // 定义帧数据类型
 * interface MyKeyframes {
 *   camera: { position: [number, number, number] }
 *   annotation: { text: string; position: [number, number] }
 * }
 * 
 * // 创建 Live 实例
 * const live = new Live<MyKeyframes>({
 *   url: 'wss://example.com/live',
 *   getTicket: async () => 'your-ticket',
 *   getVoiceSign: async (params) => ({ sdkAppId: 0, userId: '', userSig: '', roomId: '' }),
 *   useBuiltInRTC: true
 * })
 * 
 * // 监听状态变化
 * live.on('stateChange', (state, prevState) => {
 *   console.log(`状态从 ${prevState} 变为 ${state}`)
 * })
 * 
 * // 连接带看
 * await live.connect()
 * ```
 */
export class Live<KeyframesSnapshot extends Record<string, any>> extends Subscribe<EventType<KeyframesSnapshot>> {
  /**
   * 带看的配置选项
   */
  private _options: LiveOptions = {}

  /**
   * 获取带看的配置选项（只读）
   * @returns 冻结的配置选项对象
   */
  get options(): LiveOptions {
    return Object.freeze(this._options || {})
  }

  /**
   * 获取当前带看连接状态
   * @returns 当前连接状态
   */
  get state() {
    return this._controller.state
  }

  /**
   * 兼容老逻辑
   * @deprecated 请直接读取 state 属性字段，未来将移除该属性
   * @returns 当前连接状态
   */
  get readyState() {
    return this._controller.state
  }

  /**
   * 获取当前WebSocket连接的URL。(注意：这个是WebSocket连接的完整连接，附带一些query参数)。
   * @deprecated 带看不需要关注底层ws的相关信息，未来将移除
   * @returns WebSocket 连接 URL，如果未设置则返回 undefined
   */
  get url(): string | undefined {
    return this.options.url
  }

  /**
   * 获取当前 live 实例使用的 RTC 实例
   * @returns RTC 协议实例，用于语音通信
   */
  get $RTC(): RTCProtocol {
    return this._options.rtc!
  }

  /**
   * 获取当前 live 实例使用的 jsBridge 实例
   * @returns JSBridge 协议实例，用于与原生 App 通信
   */
  get jsBridge(): JSBridgeProtocol {
    return this._options.jsBridge!
  }

  /**
   * 帧数据监听器
   * 
   * 用于监听特定键的帧数据变化。当其他用户发送帧数据更新时，会触发对应键的事件。
   * 
   * @example
   * ```typescript
   * // 监听 camera 帧数据的变化
   * live.keyframes.on('camera', (nextFrame, prevFrame, frontRequestId) => {
   *   console.log('相机位置更新:', nextFrame)
   * })
   * ```
   */
  keyframes: Subscribe<KeyframeEventType<KeyframesSnapshot>> = new Subscribe()

  /**
   * 带看的 Yjs 文档实例（用于帧数据同步）
   */
  private _yDoc: Y.Doc

  /**
   * 内置的一些数据（用户信息、房间信息、帧数据等）
   */
  private _buildInData: LiveBuiltInData<KeyframesSnapshot>

  /**
   * 创建 Live 带看实例
   * 
   * @param opts 带看配置选项，包含以下属性：
   * - url: WebSocket 连接地址（可选，可在 connect 时提供）
   * - getTicket: 获取连接票据的函数（可选，可在 connect 时提供）
   * - rtc: RTC 实例（可选，如果不提供会根据配置自动创建）
   * - rtcType: 指定内置 RTC 类型（当 rtc 为空时使用）
   * - useBuiltInRTC: 是否使用内置 RTC（默认根据环境自动选择）
   * - getVoiceSign: 获取语音签名的函数（使用内置 RTC 时必需）
   * - jsBridge: JSBridge 实例（可选，用于 App/WebView 环境）
   * - useBuiltInJsBridge: 是否使用内置 JSBridge（默认 false）
   * - initialMicroStatus: 麦克风初始状态（默认 OPEN）
   * - autoPlayConfirm: 自动播放确认回调（使用 BrowserRTC 时可能需要）
   * - __debug__: 是否开启调试模式（会在控制台打印日志）
   * 
   * @example
   * ```typescript
   * // 基础用法
   * const live = new Live({
   *   url: 'wss://example.com/live',
   *   getTicket: async () => {
   *     const response = await fetch('/api/get-ticket')
   *     return response.json().ticket
   *   },
   *   getVoiceSign: async (params) => {
   *     const response = await fetch('/api/get-voice-sign', {
   *       method: 'POST',
   *       body: JSON.stringify(params)
   *     })
   *     return response.json()
   *   },
   *   useBuiltInRTC: true
   * })
   * 
   * // 使用自定义 RTC
   * const customRTC = new MyCustomRTC()
   * const live = new Live({
   *   rtc: customRTC,
   *   url: 'wss://example.com/live',
   *   getTicket: async () => 'ticket'
   * })
   * ```
   */
  constructor(opts: LiveOptions) {
    super()
    logger.info('Initializing Live instance with options:', opts) // 添加日志
    this._addLiveControllerEventListeners()
    this._yDoc = new Y.Doc({
      guid: uuid(),
    })
    this._buildInData = {
      keyframes: this._yDoc.getMap<KeyframesSnapshot>('keyframes'),
    }
    this._initializeKeyframesObservers()
    this._initializeOptions(opts)
    this._autoConnect()
    this._debugSetup()
  }
  /**
   * 获取当前所有帧数据的快照
   * 
   * @returns 当前帧数据的部分快照对象
   * @example
   * ```typescript
   * const snapshot = live.snapshot
   * console.log('当前相机位置:', snapshot.camera)
   * console.log('当前标注:', snapshot.annotation)
   * ```
   */
  get snapshot(): Partial<KeyframesSnapshot> {
    return this._buildInData.keyframes.toJSON() as Partial<KeyframesSnapshot>
  }

  /**
   * 初始化帧数据观察者（内部方法）
   * 用于监听 Yjs Map 的变化并同步到其他用户
   */
  _initializeKeyframesObservers = () => {
    this._yDoc.getMap<KeyframesSnapshot>('keyframes').observe(this._handleKeyframesChange)
  }

  /**
   * 处理帧数据变化（内部方法）
   * 当本地或远程帧数据发生变化时，会触发此方法进行同步
   * 
   * @param event Yjs Map 事件对象
   */
  _handleKeyframesChange = (event: Y.YMapEvent<KeyframesSnapshot>) => {
    const { transaction, target, changes } = event
    if (target === this._buildInData.keyframes) {
      if (transaction.origin.triggerUserId === this.selfInfo?.id) {
        // 本地操作，发送同步消息
        const liveMsg = createLiveMsg(Command.KeyframeSync, this.snapshot, undefined)
        try {
          this._controller.sendMessage(liveMsg)
        } catch (e) {
          logger.error('Failed to send keyframe sync message:', e) // 添加日志
        }
      } else {
        // 远程操作，则发布更新事件
        changes.keys.forEach((change: { action: any; oldValue: any }, key: keyof KeyframesSnapshot) => {
          const prevKeyframes = change.oldValue as KeyframesSnapshot[keyof KeyframesSnapshot]
          const nextKeyframes = this.getFramesByKey(
            key as keyof KeyframesSnapshot,
          ) as KeyframesSnapshot[keyof KeyframesSnapshot]
          const frontRequestId = transaction.origin.frontRequestId as string
          this.keyframes.emit(
            key as keyof KeyframesSnapshot,
            // @ts-expect-error 类型推断有问题
            nextKeyframes as KeyframesSnapshot[keyof KeyframesSnapshot],
            prevKeyframes as KeyframesSnapshot[keyof KeyframesSnapshot],
            frontRequestId,
          )
        })

        if (changes.keys.size !== 0) {
          this.emit('keyframes', this.snapshot, transaction.origin.frontRequestId as string)
        }
      }
    }
  }
  /**
   * 根据键值获取对应的帧数据
   * 
   * @template Key 帧数据的键类型
   * @param key 帧数据的键值
   * @returns 对应键的帧数据，如果不存在则返回 undefined
   * @example
   * ```typescript
   * // 获取相机帧数据
   * const cameraFrame = live.getFramesByKey('camera')
   * if (cameraFrame) {
   *   console.log('相机位置:', cameraFrame.position)
   * }
   * ```
   */
  getFramesByKey = <Key extends keyof KeyframesSnapshot>(key: Key): Partial<KeyframesSnapshot>[Key] | undefined =>
    this.snapshot[key]

  /**
   * 发送帧数据（需要能够被序列化）
   * 
   * 将帧数据同步到其他用户。帧数据必须是可序列化的 JSON 对象。
   * 只有在连接状态为 OPEN 时才能发送。
   * 
   * @template Key 帧数据的键类型
   * @param key 帧数据键值
   * @param frame 帧数据本体，必须可序列化
   * @returns 发送结果，成功返回 true，连接未打开返回 false，其他错误返回 Error
   * @example
   * ```typescript
   * // 发送相机位置更新
   * const result = live.sendKeyframe('camera', {
   *   position: [1, 2, 3],
   *   rotation: [0, 0, 0]
   * })
   * 
   * if (result === true) {
   *   console.log('发送成功')
   * } else {
   *   console.error('发送失败:', result)
   * }
   * 
   * // 发送标注更新
   * live.sendKeyframe('annotation', {
   *   text: '这是一个标注',
   *   position: [100, 200]
   * })
   * ```
   */
  sendKeyframe = <Key extends keyof KeyframesSnapshot>(key: Key, frame: KeyframesSnapshot[Key]): boolean | Error => {
    if (this.state !== LiveState.OPEN) {
      logger.warn('Attempted to send keyframe while connection is not open') // 添加日志
      return false
    }

    this._yDoc.transact(
      () => {
        this._buildInData.keyframes.set(key as string, frame)
      },
      {
        triggerUserId: this.selfInfo?.id,
        frontRequestId: '',
      },
    )

    return true
  }

  /**
   * 获取当前用户信息
   * 
   * @returns 当前用户信息对象，如果尚未获取则返回 undefined
   * @example
   * ```typescript
   * const selfInfo = live.selfInfo
   * if (selfInfo) {
   *   console.log('用户ID:', selfInfo.id)
   *   console.log('麦克风状态:', selfInfo.micro_status)
   *   console.log('用户权限:', selfInfo.permission)
   * }
   * ```
   */
  get selfInfo() {
    return this._buildInData.selfInfo
  }

  /**
   * 获取当前房间内的用户列表
   * 
   * @returns 用户信息数组，如果尚未获取则返回空数组
   * @example
   * ```typescript
   * const users = live.userList
   * console.log(`房间内有 ${users.length} 个用户`)
   * users.forEach(user => {
   *   console.log(`用户 ${user.id} 在线状态: ${user.current_ws_online}`)
   * })
   * ```
   */
  get userList() {
    return this._buildInData.userList || []
  }

  /**
   * 获取当前带看房间信息
   * 
   * @returns 房间信息对象，如果尚未获取则返回 undefined
   * @example
   * ```typescript
   * const roomInfo = live.roomInfo
   * if (roomInfo) {
   *   console.log('房间代码:', roomInfo.info.room_code)
   *   console.log('房间状态:', roomInfo.info.status)
   * }
   * ```
   */
  get roomInfo() {
    return this._buildInData.roomInfo
  }

  /**
   * 带看控制器（内部使用）
   */
  private _controller: LiveController = new LiveController()

  /**
   * 初始化配置选项（内部方法）
   * @param opts 配置选项
   */
  private _initializeOptions = (opts: LiveOptions) => {
    this._options = Object.assign({ initialMicroStatus: MicroStatus.OPEN }, opts)
    this._options.jsBridge = this._initializeJsBridge(opts)
    this._options.getVoiceSign = opts.getVoiceSign ?? unimplementedGetVoiceSign
    this._options.autoPlayConfirm = opts.autoPlayConfirm
    this._options.rtc = this._initializeRTC(opts)
  }

  /**
   * 初始化 JSBridge 实例（内部方法）
   * @param opts 配置选项
   * @returns JSBridge 实例或 undefined
   */
  private _initializeJsBridge = (opts: LiveOptions): JSBridgeProtocol | undefined => {
    if (opts.jsBridge) return opts.jsBridge
    if (opts.useBuiltInJsBridge) return new JSBridge()
    return undefined
  }

  /**
   * 初始化 RTC 实例（内部方法）
   * @param opts 配置选项
   * @returns RTC 实例或 undefined
   */
  private _initializeRTC = (opts: LiveOptions): RTCProtocol | undefined => {
    if (opts.rtc) return opts.rtc
    if (opts.rtcType) return this._createBuiltInRTC(opts)
    if (opts.useBuiltInRTC) return this._createDefaultRTC(opts)
    return undefined
  }

  /**
   * 创建内置 RTC 实例（内部方法）
   * @param opts 配置选项
   * @returns RTC 实例或 undefined
   */
  private _createBuiltInRTC = (opts: LiveOptions): RTCProtocol | undefined => {
    if (!opts.getVoiceSign) {
      logger.error('GetVoiceSign method must be passed when using built-in RTC instances')
      return undefined
    }
    switch (opts.rtcType) {
      case RTCType.RealseeVRWebViewRTC:
        return this._createVRWebViewRTC()
      case RTCType.RealseeBrowserChimeRTC:
        return new BrowserRTC4Chime({
          getVoiceSign: this._options.getVoiceSign ?? unimplementedGetVoiceSign,
          logLevel: opts.__debug__ ? 0 : 1,
        })
      case RTCType.RealseeBrowserRTC:
        return this._createBrowserRTC()
      default:
        logger.error('unknown built-in RTC type')
        return undefined
    }
  }

  /**
   * 创建 VRWebView RTC 实例（内部方法）
   * @returns RTC 实例或 undefined
   */
  private _createVRWebViewRTC = (): RTCProtocol | undefined => {
    if (this._options.jsBridge) {
      return new VRWebViewRTC({
        jsBridge: this._options.jsBridge,
        getVoiceSign: this._options.getVoiceSign ?? unimplementedGetVoiceSign,
      })
    } else {
      logger.error('JsBridge instance must be passed when using built-in VRWebViewRTC instances')
      return undefined
    }
  }

  /**
   * 创建浏览器 RTC 实例（内部方法）
   * @returns RTC 实例或 undefined
   */
  private _createBrowserRTC = (): RTCProtocol | undefined => {
    if (!this._options.autoPlayConfirm) {
      logger.error('autoPlayConfirm method must be passed when using built-in RealseeBrowserRTC instances')
      return undefined
    }
    return new BrowserRTC({
      getVoiceSign: this._options.getVoiceSign ?? unimplementedGetVoiceSign,
      autoPlayConfirm: this._options.autoPlayConfirm,
    })
  }

  /**
   * 创建默认 RTC 实例（内部方法）
   * 根据环境自动选择合适的 RTC 实现
   * @param opts 配置选项
   * @returns RTC 实例或 undefined
   */
  private _createDefaultRTC = (opts: LiveOptions): RTCProtocol | undefined => {
    const getVoiceSign = this._options.getVoiceSign ?? unimplementedGetVoiceSign
    // 浏览器优先：无 jsBridge 也能工作
    if (!this._options.jsBridge || (!isRealseeVRWebview && !isWxMiniProgram)) {
      return opts.builtInRTCType === BuiltInRTCType.RealseeXChimeRTC
        ? new BrowserRTC4Chime({ getVoiceSign })
        : this._createBrowserRTC()
    }

    // App/WebView or MiniProgram
    if ((isRealseeVRWebview && (isAndroid || isIOS)) || isWxMiniProgram) {
      return this._createVRWebViewRTC()
    }

    return this._createBrowserRTC()
  }

  /**
   * 自动连接（内部方法）
   * 如果配置中已提供 url 和 getTicket，则自动连接
   */
  private _autoConnect = () => {
    if (this._options.url && this._options.getTicket) {
      this.connect()
    }
  }

  /**
   * 调试模式设置（内部方法）
   * 在调试模式下，将实例挂载到 window 对象上
   */
  private _debugSetup = () => {
    if (this._options.__debug__) {
      Object.assign(window, { $liveController: this._controller, $live: this })
    }
  }

  /**
   * 添加带看控制的事件监听
   */
  private _addLiveControllerEventListeners = () => {
    this._controller.on('stateChange', this._handleStateChange)
    this._controller.on('message', this._handleMessage)
  }

  /**
   * 移除带看控制的事件监听
   */
  private _removeLiveControllerEventListeners = () => {
    this._controller.off('stateChange', this._handleStateChange)
    this._controller.off('message', this._handleMessage)
  }

  /**
   * 带看状态变化处理
   * @param state 当前状态
   * @param prevState 之前的状态
   */
  private _handleStateChange = (state: LiveState, prevState: LiveState) => {
    logger.info(`State changed from ${prevState} to ${state}`) // 添加日志
    this.emit('stateChange', state, prevState)
  }

  /**
   * 消息接收
   * @param liveMsg
   */
  private _handleMessage = async (liveMsg: LiveMsg) => {
    logger.debug('Received message:', liveMsg) // 添加日志
    if (typeof liveMsg === 'object') {
      const { command, data = {}, frontRequestId, code, message, triggerUserId } = liveMsg
      if (code === 'SUCCESS') {
        switch (command) {
          /**
           * 自己的状态变化
           */
          case Command.SelfUserInfo: {
            const selfInfo = data as UserInfo
            if (!equal(this.selfInfo, selfInfo, { deep: true })) {
              this._buildInData.selfInfo = selfInfo
              this.emit('selfInfoUpdate', this._buildInData.selfInfo, frontRequestId)
            }
            break
          }

          /**
           * 房间信息变化
           */
          case Command.RoomInfo: {
            const roomInfo = data as RoomInfo
            this._buildInData.roomInfo = Object.assign({}, roomInfo)
            const lastFrame = roomInfo.last_frame || {}
            // 这里应该直接去设置本地的帧数据
            this._yDoc.transact(
              () => {
                for (const key in lastFrame) {
                  this._buildInData.keyframes.set(key, lastFrame[key])
                }
              },
              {
                triggerUserId: 'readyKeyframeSync',
                frontRequestId,
              },
            )
            this.emit('readyKeyframeSync', (roomInfo.last_frame || {}) as Partial<KeyframesSnapshot>)
            break
          }

          /**
           * 语音信息变化
           */
          case Command.RTCInfo: {
            logger.info('RTC info updated:', data) // 添加日志
            const { voice_id: voiceId, type } = data as {
              voice_id: string
              type: RTCType
            }
            this._buildInData.rtcInfo = { voiceId, type }
            if (!this.$RTC?.joined) {
              const result = await this.$RTC?.join({
                voiceId,
                userId: this.selfInfo?.id || '',
                roomId: this.roomInfo?.info.room_code,
                type,
              })
              if (result) {
                const status = this._options.initialMicroStatus ?? MicroStatus.OPEN
                await this.toggleMicro(status === MicroStatus.OPEN)
              } else {
                logger.error('加入语音房间失败')
              }
            } else {
              logger.warn('已加入语音房间，无需重复加入！')
            }
            break
          }

          /**
           * 房间用户列表变化
           */
          case Command.UserList: {
            const userList = data as UserInfo[]
            this._buildInData.userList = userList
            this.emit('userListUpdate', this.userList, frontRequestId)
            break
          }

          /**
           * 自定义事件广播
           */
          case Command.CustomEvent: {
            const evtMsg = data as Record<string, any>
            this.emit('broadcast', evtMsg, frontRequestId)
            break
          }

          /**
           * 帧数据同步
           */
          case Command.KeyframeSync: {
            const remoteSnapshot = data as Partial<KeyframesSnapshot>
            if (triggerUserId === this.selfInfo?.id) {
              // 自己发送的帧数据同步，不处理
              return
            }
            this._yDoc.transact(
              () => {
                // 直接更新，yDoc会自动处理冲突
                for (const key in remoteSnapshot) {
                  this._buildInData.keyframes.set(
                    key as string,
                    remoteSnapshot[key] as KeyframesSnapshot[keyof KeyframesSnapshot],
                  )
                }
              },
              {
                triggerUserId,
                frontRequestId,
              },
            )
            break
          }

          /**
           * 内置消息
           */
          case Command.BuiltinMsg: {
            const builtinMsg = data as BuiltinMsg
            this.emit('builtinEvent', builtinMsg)

            if (builtinMsg.event_type === BuiltinEventType.UserChange) {
              this.emit('userChange', builtinMsg)
            }

            if (builtinMsg.event_type === BuiltinEventType.RoomChange) {
              this.emit('roomChange', builtinMsg)
            }
            /**
             * 下发指令自己操作麦克风
             */
            if (
              builtinMsg.event_type === BuiltinEventType.UserChange &&
              builtinMsg.action === BuiltinActionType.UserMicroStatus
            ) {
              await this.toggleMicro(builtinMsg.data.status === MicroStatus.OPEN)
            }

            /**
             * 如果下发指令退出房间，则 关闭麦克风 及 销毁 WebSocket
             */
            if (
              builtinMsg.event_type === BuiltinEventType.RoomChange &&
              builtinMsg.action === BuiltinActionType.RoomClose
            ) {
              await this._exit()
            }
            break
          }
          /**
           * 被踢出房间
           */
          case Command.UserKickOut: {
            this.emit('kickOut')
            break
          }
          /**
           * 其它未知广播
           */
          default: {
            logger.warn('Unknown command received:', command) // 添加日志
          }
        }
      } else {
        logger.error('Error in message:', message) // 添加日志
        this.emit('error', liveMsg)
      }
    }
  }

  /**
   * 清理带看缓存的数据
   */
  private _clearLiveData = () => {
    this._buildInData.keyframes.clear()
    this._buildInData = { keyframes: this._yDoc.getMap('keyframes') }
  }

  /**
   * 连接带看房间
   * 
   * 建立 WebSocket 连接并加入带看房间。如果配置中已提供 url 和 getTicket，
   * 可以在创建实例后自动连接，也可以手动调用此方法。
   * 
   * @param options 连接选项，包含以下属性：
   * - url: WebSocket 连接地址（可选，会覆盖构造函数中的配置）
   * - getTicket: 获取连接票据的函数（可选，会覆盖构造函数中的配置）
   * - rtcMicStatus: 麦克风初始状态（可选，会覆盖构造函数中的配置）
   * - force: 是否强制连接（如果已有连接，会先关闭再连接）
   * @returns 连接结果，成功返回 true，失败返回 Error
   * @example
   * ```typescript
   * // 在创建实例时已配置 url 和 getTicket
   * const live = new Live({
   *   url: 'wss://example.com/live',
   *   getTicket: async () => 'ticket'
   * })
   * 
   * // 手动连接
   * const result = await live.connect()
   * if (result === true) {
   *   console.log('连接成功')
   * } else {
   *   console.error('连接失败:', result.message)
   * }
   * 
   * // 在连接时提供配置
   * await live.connect({
   *   url: 'wss://example.com/live',
   *   getTicket: async () => {
   *     const res = await fetch('/api/ticket')
   *     return res.json().ticket
   *   },
   *   rtcMicStatus: MicroStatus.OPEN
   * })
   * 
   * // 强制重连
   * await live.connect({ force: true })
   * ```
   */
  connect = async (options: LiveConnectOptions = {}) => {
    logger.info('Attempting to connect with options:', options) // 添加日志
    // 检查 url 和 票据获取接口实现
    this._options.url = options.url ?? this._options.url ?? ''
    this._options.getTicket = options.getTicket ?? this._options.getTicket
    this._options.initialMicroStatus = options.rtcMicStatus ?? this._options.initialMicroStatus

    if (!/^wss?:\/\//.test(this._options.url!) || typeof this._options.getTicket !== 'function') {
      return new Error('Invalid connection parameters') // 优化：简化错误信息
    }

    // 检测麦克风权限
    if (this._options.rtc) {
      const res = await this._options.rtc.detectMicro()
      if (!res) {
        return new Error('Failed to obtain microphone authorization')
      }
    }

    // 检查是否已有实例在使用
    const currentState = this.state
    if (currentState === LiveState.CONNECTING || currentState === LiveState.OPEN) {
      if (options.force) {
        await this._controller.close('force close by connect').catch(console.warn)
      } else {
        return new Error('已有Live实例正在连接中，请勿重复连接！')
      }
    }

    // 建立连接
    this._clearLiveData()
    try {
      const ticket = await this._options.getTicket()
      const url = appendUri(this._options.url!, {
        ticket,
        force: Number(!!options.force),
      })
      this._controller.open(url)
      logger.info('Connection established successfully') // 添加日志
      return true
    } catch (e) {
      logger.error('Failed to connect:', e) // 添加日志
      return new Error('Failed to connect')
    }
  }

  /**
   * 退出带看（内部方法）
   * 清理数据并关闭连接，但不发送房间关闭消息
   * 
   * @returns Promise<boolean> 关闭是否成功
   */
  _exit = async () => {
    this._clearLiveData()
    try {
      /** 退出强制关闭语音 */
      await this.$RTC?.quit()
    } catch (e) {
      logger.warn(e)
    }
    if (this.state !== LiveState.OPEN) return true
    return await this._controller.close()
  }

  /**
   * 退出带看房间
   * 
   * 这是 close 方法的别名，用于保持 API 的一致性。
   * 
   * @returns Promise<void>
   * @example
   * ```typescript
   * // 退出带看
   * await live.exit()
   * ```
   */
  exit = async () => this.close()

  /**
   * 关闭带看房间
   * 
   * 关闭 WebSocket 连接、退出 RTC 房间，并发送房间关闭消息。
   * 与 exit 方法不同的是，此方法会通知服务器房间已关闭。
   * 
   * @returns Promise<void>
   * @example
   * ```typescript
   * // 关闭房间
   * await live.close()
   * 
   * // 在组件卸载时关闭
   * useEffect(() => {
   *   return () => {
   *     live.close()
   *   }
   * }, [])
   * ```
   */
  close = async () => {
    await this._controller.readyClose()
    try {
      /** 退出强制关闭语音 */
      await this.$RTC?.quit()
    } catch (e) {
      logger.warn(e)
    }
    await this.updateStatus(BuiltinEventType.RoomChange, BuiltinActionType.RoomClose, {})
  }

  /**
   * 销毁带看实例
   * 
   * 完全销毁实例，清理所有资源，包括事件监听、Yjs 文档等。
   * 销毁后实例不能再使用，需要重新创建。
   * 
   * @returns Promise<void>
   * @example
   * ```typescript
   * // 销毁实例
   * await live.dispose()
   * 
   * // 在组件卸载时销毁
   * useEffect(() => {
   *   return () => {
   *     live.dispose()
   *   }
   * }, [])
   * ```
   */
  dispose = async () => {
    this._removeLiveControllerEventListeners()
    // 解绑 yDoc 观察者并销毁文档
    try {
      this._yDoc.getMap<KeyframesSnapshot>('keyframes').unobserve(this._handleKeyframesChange)
      this._yDoc.destroy()
    } catch (e) {
      logger.warn(e)
    }
    this._clearLiveData()
    return await this._controller.dispose()
  }

  /**
   * 更新带看状态
   * 
   * 发送内置消息来更新用户或房间的状态。只有在连接状态为 OPEN 时才能发送。
   * 
   * @param evtType 事件类型（用户变化或房间变化）
   * @param atnType 操作类型（如用户信息、麦克风状态、房间关闭等）
   * @param status 状态数据对象
   * @param toUserIds 目标用户ID（可选，不传则广播给所有人）
   * @returns 是否发送成功，连接未打开或发送失败返回 false
   * @example
   * ```typescript
   * // 更新用户信息
   * await live.updateStatus(
   *   BuiltinEventType.UserChange,
   *   BuiltinActionType.UserInfo,
   *   { nickname: '新昵称', avatar: 'https://example.com/avatar.jpg' }
   * )
   * 
   * // 关闭房间（仅管理员可用）
   * await live.updateStatus(
   *   BuiltinEventType.RoomChange,
   *   BuiltinActionType.RoomClose,
   *   {}
   * )
   * 
   * // 向特定用户发送状态更新
   * await live.updateStatus(
   *   BuiltinEventType.UserChange,
   *   BuiltinActionType.UserMicroStatus,
   *   { status: MicroStatus.CLOSE },
   *   ['user-id-1', 'user-id-2']
   * )
   * ```
   */
  async updateStatus(
    evtType: BuiltinEventType,
    atnType: BuiltinActionType,
    status: Record<string, any>,
    toUserIds?: string | string[],
  ) {
    if (this.state !== LiveState.OPEN) return false
    const data = Object.assign({ event_type: evtType, action: atnType }, { data: status })
    const liveMsg = createLiveMsg(Command.BuiltinMsg, data, toUserIds)
    try {
      this._controller.sendMessage(liveMsg)
      return true
    } catch (e) {
      logger.error(e)
      return false
    }
  }

  /**
   * 设置自己的用户信息
   * 
   * 更新当前用户在房间中的信息，如昵称、头像等。
   * 
   * @param userInfo 用户信息的部分更新对象
   * @returns Promise<boolean> 是否更新成功
   * @example
   * ```typescript
   * // 更新昵称和头像
   * await live.setSelfInfo({
   *   extension: {
   *     nickname: '新昵称',
   *     avatar: 'https://example.com/avatar.jpg'
   *   }
   * })
   * ```
   */
  setSelfInfo = async (userInfo: Partial<UserInfo>) =>
    await this.updateStatus(BuiltinEventType.UserChange, BuiltinActionType.UserInfo, userInfo)

  /**
   * 自定义广播消息
   * 
   * 向指定用户发送自定义消息，并等待响应。如果设置了超时时间，
   * 超时后会拒绝 Promise。
   * 
   * @template Request 请求数据类型
   * @template Response 响应数据类型
   * @param request 广播请求的数据对象
   * @param toUserIds 目标用户ID，可以是单个字符串或字符串数组，空数组表示广播给所有人
   * @param timeout 超时时间（毫秒），0 表示不超时
   * @returns Promise<Response> 响应数据，连接未打开返回 false，发送失败返回 Error
   * @example
   * ```typescript
   * // 发送自定义消息并等待响应
   * try {
   *   const response = await live.broadcast(
   *     { type: 'ping', data: 'hello' },
   *     ['user-id-1', 'user-id-2'],
   *     5000 // 5秒超时
   *   )
   *   console.log('收到响应:', response)
   * } catch (error) {
   *   console.error('广播失败或超时:', error)
   * }
   * 
   * // 广播给所有人
   * const response = await live.broadcast(
   *   { type: 'announcement', message: '大家好' },
   *   [],
   *   0 // 不超时
   * )
   * 
   * // 监听广播消息
   * live.on('broadcast', (data, frontRequestId) => {
   *   console.log('收到广播:', data)
   *   // 可以在这里响应广播
   * })
   * ```
   */
  broadcast = <Request extends Record<string, any>, Response>(
    request: Request,
    toUserIds: string | string[],
    timeout: number,
  ): Promise<Response> | Error | false => {
    if (this.state !== LiveState.OPEN) return false
    return new Promise((resolve, reject) => {
      const customEvent = createLiveMsg(Command.CustomEvent, request, toUserIds)
      const front_request_id = customEvent.front_request_id

      const timeoutId = timeout
        ? setTimeout(() => {
            reject(new Error('RESPONSE TIMEOUT'))
            this.off('broadcast', broadcastRes)
          }, timeout)
        : undefined

      const broadcastRes = (data: Record<string, any>, frontRequestId: string) => {
        if (frontRequestId !== front_request_id) return
        if (timeoutId) clearTimeout(timeoutId)
        this.off('broadcast', broadcastRes)
        resolve(data as Response)
      }

      this.on('broadcast', broadcastRes)

      try {
        this._controller.sendMessage(customEvent)
      } catch (e) {
        reject(e)
      }
    })
  }

  /**
   * 切换自己的麦克风状态
   * 
   * 开启或关闭自己的麦克风。只有拥有 control_own_micro 权限的用户才能操作。
   * 
   * @param microStatus 麦克风状态，true 表示开启，false 表示关闭
   * @returns Promise<boolean> 是否操作成功，无权限返回 false
   * @example
   * ```typescript
   * // 开启麦克风
   * const result = await live.toggleMicro(true)
   * if (result) {
   *   console.log('麦克风已开启')
   * }
   * 
   * // 关闭麦克风
   * await live.toggleMicro(false)
   * 
   * // 检查权限
   * if (live.selfInfo?.permission?.control_own_micro) {
   *   await live.toggleMicro(true)
   * } else {
   *   console.log('没有权限控制麦克风')
   * }
   * ```
   */
  toggleMicro = async (microStatus: boolean) => {
    if (!this.selfInfo?.permission?.control_own_micro) return false

    const result = await this.$RTC?.toggleMicro(microStatus)
    if (result) {
      await this.updateStatus(
        BuiltinEventType.UserChange,
        BuiltinActionType.UserMicroStatus,
        {
          status: microStatus ? MicroStatus.OPEN : MicroStatus.CLOSE,
        },
        [],
      )
    }

    return result
  }

  /**
   * 控制其他用户的麦克风状态
   * 
   * 开启或关闭指定用户的麦克风。只有拥有 control_other_micro 权限的用户才能操作。
   * 
   * @param microStatus 麦克风状态（MicroStatus.OPEN 或 MicroStatus.CLOSE）
   * @param toUserIds 目标用户ID，可以是单个字符串或字符串数组
   * @returns Promise<boolean> 是否操作成功，无权限或权限检查失败返回 false
   * @example
   * ```typescript
   * // 关闭某个用户的麦克风
   * await live.forbidMicro(MicroStatus.CLOSE, 'user-id-1')
   * 
   * // 开启多个用户的麦克风
   * await live.forbidMicro(MicroStatus.OPEN, ['user-id-1', 'user-id-2'])
   * 
   * // 检查权限
   * if (live.selfInfo?.permission?.control_other_micro) {
   *   await live.forbidMicro(MicroStatus.CLOSE, 'user-id-1')
   * }
   * ```
   */
  forbidMicro = async (microStatus: MicroStatus, toUserIds: string | string[]) => {
    if (typeof this._options.canForbidUserMicro === 'function' && !this._options.canForbidUserMicro(toUserIds)) {
      return false
    }

    if (!this.selfInfo?.permission?.control_other_micro) {
      return false
    }

    return await this.updateStatus(
      BuiltinEventType.UserChange,
      BuiltinActionType.UserMicroStatus,
      {
        status: microStatus,
      },
      toUserIds,
    )
  }

  /**
   * 控制其他用户的帧数据同步权限
   * 
   * 允许或禁止指定用户同步全量帧数据。只有拥有 control_other_sync 权限的用户才能操作。
   * 
   * @param sync 是否允许同步，true 表示允许，false 表示禁止
   * @param toUserIds 目标用户ID，可以是单个字符串或字符串数组
   * @returns Promise<boolean> 是否操作成功，无权限或权限检查失败返回 false
   * @example
   * ```typescript
   * // 禁止某个用户同步帧数据
   * await live.forbidSync(false, 'user-id-1')
   * 
   * // 允许多个用户同步帧数据
   * await live.forbidSync(true, ['user-id-1', 'user-id-2'])
   * ```
   */
  forbidSync = async (sync: boolean, toUserIds: string | string[]) => {
    if (typeof this._options.canForbidUserSync === 'function' && !this._options.canForbidUserSync(toUserIds)) {
      return false
    }

    if (!this.selfInfo?.permission?.control_other_sync) {
      return false
    }

    return await this.updateStatus(
      BuiltinEventType.UserChange,
      BuiltinActionType.UserPermission,
      { sync_frame: sync },
      toUserIds,
    )
  }

  /**
   * 将用户踢出带看房间
   * 
   * 强制将指定用户从房间中移除。只有拥有 change_other_status 权限的用户才能操作。
   * 
   * @param toUserIds 目标用户ID，可以是单个字符串或字符串数组
   * @returns Promise<boolean> 是否操作成功，无权限或权限检查失败返回 false
   * @example
   * ```typescript
   * // 踢出单个用户
   * await live.kick('user-id-1')
   * 
   * // 踢出多个用户
   * await live.kick(['user-id-1', 'user-id-2'])
   * 
   * // 检查权限
   * if (live.selfInfo?.permission?.change_other_status) {
   *   await live.kick('user-id-1')
   * }
   * ```
   */
  kick = async (toUserIds: string | string[]) => {
    if (typeof this._options.canForbidUserKick === 'function' && !this._options.canForbidUserKick(toUserIds)) {
      return false
    }

    if (!this.selfInfo?.permission?.change_other_status) {
      return false
    }

    return await this.updateStatus(
      BuiltinEventType.UserChange,
      BuiltinActionType.UserStatus,
      {
        status: 'KICK_OUT',
      },
      toUserIds,
    )
  }

  /**
   * 获取指定键的帧数据快照
   * 
   * 这是 getFramesByKey 方法的别名，用于保持 API 的一致性。
   * 
   * @template Key 帧数据的键类型
   * @param key 帧数据的键值
   * @returns 对应键的帧数据，如果不存在则返回 undefined
   * @example
   * ```typescript
   * const cameraFrame = live.getSnapshotForKey('camera')
   * ```
   */
  getSnapshotForKey = <Key extends keyof KeyframesSnapshot>(key: Key): any => this.snapshot[key]
}
